import axios from "axios";
import { isAndroid, isIOS } from "react-device-detect";

import { BASE_URL } from "../../actions";

export const APP_DEEP_LINK_SCHEME = "biosynciq";

export const getEnrollmentPlatform = () => {
  if (isIOS) return "ios";
  if (isAndroid) return "android";
  return "web";
};

export const isMobileDevice = () => isIOS || isAndroid;

export const buildEnrollmentAppUrl = (enrollmentToken) =>
  `${APP_DEEP_LINK_SCHEME}://practice_detail?enrollment_token=${encodeURIComponent(enrollmentToken)}`;

export const buildPracticeTokenAppUrl = (token) =>
  `${APP_DEEP_LINK_SCHEME}://practice_detail?token=${encodeURIComponent(token)}`;

export const MISSING_ENROLLMENT_MESSAGE =
  "Please scan the practice QR code first, then use this form to sign up.";

export const INVALID_ENROLLMENT_LINK_MESSAGE =
  "This QR code is no longer valid. Please scan a new QR code to continue.";

export const INVALID_LINK_MESSAGE =
  "This link is no longer valid. Please request a new link to continue.";

const getLinkErrorMessage = (error, fallbackMessage) => {
  const apiMessage =
    error.response?.data?.msg ||
    error.response?.data?.message ||
    error.response?.data?.error;

  if (
    error.response?.status === 404 ||
    error.response?.status === 410 ||
    apiMessage === "Invalid or expired enrollment link" ||
    apiMessage === "Invalid link" ||
    apiMessage === "This link has expired" ||
    error.message === "Invalid or expired enrollment link" ||
    error.message === "Invalid link"
  ) {
    return fallbackMessage;
  }

  if (apiMessage) {
    return apiMessage;
  }

  return error.message || fallbackMessage;
};

const resolveToken = async (token, { source, fallbackMessage } = {}) => {
  const platform = getEnrollmentPlatform();
  const params = new URLSearchParams({ platform });
  if (source) {
    params.set("source", source);
  }

  try {
    const response = await axios.get(
      `${BASE_URL}/resolve-token/${encodeURIComponent(token)}?${params.toString()}`,
    );

    if (!response?.data?.ok) {
      throw new Error(response?.data?.msg || fallbackMessage);
    }

    return {
      agencyId: response?.data?.agency_id,
      agencyName: response?.data?.agency_name,
      patientId: response?.data?.patient_id,
      purpose: response?.data?.purpose,
    };
  } catch (error) {
    throw new Error(getLinkErrorMessage(error, fallbackMessage));
  }
};

export const resolveEnrollmentToken = async (token) =>
  resolveToken(token, {
    source: "qr",
    fallbackMessage: INVALID_ENROLLMENT_LINK_MESSAGE,
  });

export const resolvePracticeToken = async (token) =>
  resolveToken(token, {
    source: "qr",
    fallbackMessage: INVALID_LINK_MESSAGE,
  });

export const buildEnrollmentQuery = ({ enrollmentToken, agencyName }) => {
  const params = new URLSearchParams({ enrollment_token: enrollmentToken });
  if (agencyName) {
    params.set("agency_name", agencyName);
  }
  return params.toString();
};

const isBlank = (value) => !value || !String(value).trim();
const isInvalidAgencyId = (agencyId) =>
  isBlank(agencyId) || agencyId === "null" || agencyId === "undefined";

export const isMissingEnrollmentContext = ({
  enrollmentToken,
  agencyId,
  agencyName,
}) => {
  if (enrollmentToken) return false;

  return isInvalidAgencyId(agencyId) && isBlank(agencyName);
};
